import * as React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogHeading, Typography } from 'omakase';
import { IconApple, IconFacebook, IconGoogle } from 'ui/icons';
import { IconLogoApple, IconLogoFacebook, IconLogoGoogle } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { ifClickable } from '@tock/shared/utils/touchUtils';

export type SupportedSocialLogin = 'facebook' | 'google' | 'apple';

interface Props {
  network: SupportedSocialLogin;
  text: string;
  analyticsEventLabel: string;
  onClick(): void;
}

type WhichInstructionsType = 'none' | 'ios' | 'android';

interface EmbeddedCannotProceedDialogProps {
  whichInstructions: WhichInstructionsType;
  hideInstructions: () => void;
  buttonText: string;
}

interface HasStandalone {
  standalone: boolean;
}

const SocialLoginButton = ({ network, text, analyticsEventLabel, onClick }: Props) => {
  const [whichInstructions, setWhichInstructions] = React.useState<WhichInstructionsType>('none');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    // Show a helpful alert if the user seems to be using an embedded browser
    const standalone =
      'standalone' in window.navigator && (window.navigator as unknown as HasStandalone).standalone;
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMobileNative = /tock\/(react native\/)?(ios|android)/.test(userAgent);
    const isSafari = /safari/.test(userAgent);
    const isIos = /iphone|ipod|ipad/.test(userAgent);

    if (!isMobileNative) {
      if (isIos && !standalone && !isSafari) {
        setWhichInstructions('ios');
        return;
      }

      if (userAgent.includes('wv')) {
        setWhichInstructions('android');
        return;
      }
    }

    onClick();
  };

  return (
    <>
      <Button
        size="large"
        fullWidth
        variant="basic"
        iconStart={<SocialLoginIcon network={network} />}
        onClick={ifClickable(handleClick, analyticsEventLabel)}
        css={({ spacing }: Theme) => css`
          margin-bottom: ${spacing(1.5)}px;
        `}
      >
        {text}
      </Button>
      <EmbeddedCannotProceedDialog
        whichInstructions={whichInstructions}
        hideInstructions={() => setWhichInstructions('none')}
        buttonText={text}
      />
    </>
  );
};

const SocialLoginIcon = ({ network }: Pick<Props, 'network'>) => {
  switch (network) {
    case 'facebook':
      return <IconMigrationUtil legacyIcon={<IconFacebook />} newIcon={<IconLogoFacebook />} />;
    case 'google':
      return <IconMigrationUtil legacyIcon={<IconGoogle />} newIcon={<IconLogoGoogle />} />;
    case 'apple':
      return <IconMigrationUtil legacyIcon={<IconApple />} newIcon={<IconLogoApple />} />;
    default:
      assertNever(network);
  }
};

const EmbeddedCannotProceedDialog = ({
  whichInstructions,
  hideInstructions,
  buttonText,
}: EmbeddedCannotProceedDialogProps) => (
  <Dialog open={whichInstructions !== 'none'} onClose={hideInstructions} variant="partial">
    <DialogHeading onClose={hideInstructions} />
    <DialogContent>
      <Typography component="p" variant="body02" mb={2}>
        You appear to be visiting Tock using a browser embedded in another mobile application.
      </Typography>
      <Typography component="p" variant="body02" mb={2}>
        For example, you might be visiting using the Instragram app.
      </Typography>
      <Typography variant="body02" mb={3}>
        {whichInstructions === 'ios' ? (
          <>
            We recommend that you use the ⋮ button in the upper-right corner of the screen and
            select <strong>Open in Safari</strong>, then tap `{buttonText}` again to log in.
          </>
        ) : (
          <>
            We recommend that you use the ⋮ button in the upper-right corner of the screen and
            select <strong>Open in Chrome</strong>, then tap `{buttonText}` again to log in.
          </>
        )}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button fullWidth size="medium" variant="primary" onClick={hideInstructions}>
        OK
      </Button>
    </DialogActions>
  </Dialog>
);

export default SocialLoginButton;
